import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { submitSearchInquiry } from "@/utils/submitSearchInquiry";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * PersonalizedSearchEnquiry — lead-capture search form (not a listing filter).
 * Step 1: preferred location / criteria hint
 * Step 2: contact details → saved in admin via /api/search-inquiry
 */
const PersonalizedSearchEnquiry = ({
  searchType = "recherche",
  sourcePage = "/",
  searchMode,
  label,
  title,
  intro,
  helperText,
  addressPlaceholder,
  continueLabel,
  testIdPrefix = "search-entry",
}) => {
  const { language } = useLanguage();
  const [step, setStep] = useState("address"); // address | details | success
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const copy = {
    label:
      label ||
      (language === "fr" ? "Recherche personnalisée" : "Personalized search"),
    title:
      title ||
      (language === "fr" ? "Recherchez votre bien idéal" : "Find your ideal property"),
    intro:
      intro ||
      (language === "fr"
        ? "Laissez-nous vous guider vers la propriété qui correspond à vos attentes."
        : "Let us guide you to the property that matches your expectations."),
    helperText:
      helperText !== undefined
        ? helperText
        : language === "fr"
          ? "Nous avons plusieurs biens off-market disponibles — n'hésitez pas à nous contacter pour en savoir plus."
          : "We have several off-market properties available — do not hesitate to enquire for more information.",
    addressPlaceholder:
      addressPlaceholder ||
      (language === "fr"
        ? "Ville, région ou adresse souhaitée..."
        : "City, region or preferred address..."),
    continue: continueLabel || (language === "fr" ? "Continuer" : "Continue"),
    submit: language === "fr" ? "Envoyer ma demande" : "Send enquiry",
    sending: language === "fr" ? "Envoi..." : "Sending...",
    firstName: language === "fr" ? "Prénom" : "First name",
    lastName: language === "fr" ? "Nom" : "Last name",
    email: "Email",
    phone: language === "fr" ? "Téléphone" : "Phone",
    message: language === "fr" ? "Message (optionnel)" : "Message (optional)",
    messagePlaceholder:
      language === "fr"
        ? "Précisez vos critères, budget, ou toute information utile..."
        : "Share criteria, budget, or any useful details...",
    successTitle: language === "fr" ? "Merci pour votre demande" : "Thank you for your enquiry",
    successBody:
      language === "fr"
        ? "Notre équipe vous recontactera sous peu avec des biens correspondant à vos critères."
        : "Our team will get back to you shortly with properties matching your criteria.",
    back: language === "fr" ? "Modifier l'adresse" : "Edit address",
  };

  const handleAddressContinue = (e) => {
    e.preventDefault();
    if (!formData.address.trim()) {
      toast.error(
        language === "fr"
          ? "Veuillez indiquer une adresse ou une région"
          : "Please enter an address or region"
      );
      return;
    }
    setStep("details");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim()
    ) {
      toast.error(
        language === "fr"
          ? "Veuillez remplir tous les champs obligatoires"
          : "Please fill in all required fields"
      );
      return;
    }

    setSubmitting(true);
    try {
      await submitSearchInquiry({
        searchType,
        sourcePage,
        searchMode,
        filters: { location: formData.address.trim() },
        contact: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        },
      });

      setStep("success");
      toast.success(
        language === "fr"
          ? "Votre demande a bien été envoyée"
          : "Your enquiry has been sent"
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      const fallback = language === "fr" ? "Échec de l'envoi" : "Failed to send enquiry";
      toast.error(typeof detail === "string" ? detail : fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    "w-full px-4 py-3 bg-white border border-[#E5E5E0] text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#8B7355] transition-colors text-sm font-light";

  return (
    <section className="relative" data-testid={testIdPrefix}>
      <div className="py-10 md:py-14 bg-byron-bg">
        <div className="container-byron">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="label-text mb-3">{copy.label}</p>
              <h2 className="heading-primary mb-4">{copy.title}</h2>
              <p className="body-text mb-3">{copy.intro}</p>
              {copy.helperText && (
                <p className="body-text mb-6 text-[#8B7355]">{copy.helperText}</p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              {step === "success" ? (
                <div className="py-8" data-testid={`${testIdPrefix}-success`}>
                  <div className="w-12 h-12 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center mx-auto mb-5">
                    <Check size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="heading-secondary mb-3">{copy.successTitle}</h3>
                  <p className="body-text">{copy.successBody}</p>
                </div>
              ) : step === "address" ? (
                <form onSubmit={handleAddressContinue}>
                  <div className="mb-4">
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(address) => setFormData({ ...formData, address })}
                      placeholder={copy.addressPlaceholder}
                      variant="boxed"
                      types={["geocode"]}
                      preferLocality
                      testId={`${testIdPrefix}-location-input`}
                    />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#2A2A2A] transition-colors whitespace-nowrap"
                    data-testid={`${testIdPrefix}-cta`}
                  >
                    {copy.continue}
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </button>
                </form>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="text-left space-y-4"
                  data-testid={`${testIdPrefix}-details`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-[#555]">
                      <span className="text-[#1A1A1A] font-medium">{formData.address}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => setStep("address")}
                      className="text-xs uppercase tracking-[0.12em] text-[#8B7355] hover:text-[#1A1A1A] transition-colors"
                    >
                      {copy.back}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label-text mb-2 block">{copy.firstName}</label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className={fieldClass}
                        data-testid={`${testIdPrefix}-first-name`}
                      />
                    </div>
                    <div>
                      <label className="label-text mb-2 block">{copy.lastName}</label>
                      <input
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className={fieldClass}
                        data-testid={`${testIdPrefix}-last-name`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label-text mb-2 block">{copy.email}</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={fieldClass}
                        data-testid={`${testIdPrefix}-email`}
                      />
                    </div>
                    <div>
                      <label className="label-text mb-2 block">{copy.phone}</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className={fieldClass}
                        data-testid={`${testIdPrefix}-phone`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label-text mb-2 block">{copy.message}</label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={copy.messagePlaceholder}
                      className={`${fieldClass} resize-none`}
                      data-testid={`${testIdPrefix}-message`}
                    />
                  </div>

                  <div className="text-center pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#2A2A2A] transition-colors disabled:opacity-60"
                      data-testid={`${testIdPrefix}-submit`}
                    >
                      {submitting ? copy.sending : copy.submit}
                      {!submitting && <ArrowRight size={14} strokeWidth={1.5} />}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalizedSearchEnquiry;
